import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import './OverviewDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const OverviewDashboard = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const [claudeRes, geminiRes, openaiRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/metrics/summary?days=${days}`).catch(() => ({ data: null })),
        axios.get(`${API_BASE_URL}/metrics/summary?days=${days}&customer_type=gemini`).catch(() => ({ data: null })),
        axios.get(`${API_BASE_URL}/metrics/summary?days=${days}&customer_type=openai`).catch(() => ({ data: null })),
      ]);

      // Fetch cost trends for chart
      const [claudeCostRes, geminiCostRes, openaiCostRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/metrics/cost-trends?days=30`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_BASE_URL}/metrics/cost-trends?days=30&customer_type=gemini`).catch(() => ({ data: { data: [] } })),
        axios.get(`${API_BASE_URL}/metrics/cost-trends?days=30&customer_type=openai`).catch(() => ({ data: { data: [] } })),
      ]);

      setOverviewData({
        claude: claudeRes.data,
        gemini: geminiRes.data,
        openai: openaiRes.data,
        costTrends: {
          claude: claudeCostRes.data.data || [],
          gemini: geminiCostRes.data.data || [],
          openai: openaiCostRes.data.data || [],
        }
      });
    } catch (err) {
      console.error('Error fetching overview data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
    const interval = setInterval(fetchOverviewData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [days]);

  if (loading) {
    return (
      <div className="overview-dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  // Calculate totals
  const totalCost = (overviewData?.claude?.total_cost_usd || 0) +
                    (overviewData?.gemini?.total_cost_usd || 0) +
                    (overviewData?.openai?.total_cost_usd || 0);

  const totalSessions = (overviewData?.claude?.total_sessions || 0) +
                        (overviewData?.gemini?.total_sessions || 0) +
                        (overviewData?.openai?.total_sessions || 0);

  const totalTokens = (overviewData?.claude?.total_input_tokens || 0) +
                      (overviewData?.claude?.total_output_tokens || 0) +
                      (overviewData?.gemini?.total_input_tokens || 0) +
                      (overviewData?.gemini?.total_output_tokens || 0) +
                      (overviewData?.openai?.total_input_tokens || 0) +
                      (overviewData?.openai?.total_output_tokens || 0);

  // Prepare combined cost trend chart data
  const getCombinedDates = () => {
    const allDates = new Set();
    overviewData?.costTrends?.claude?.forEach(d => allDates.add(d.date));
    overviewData?.costTrends?.gemini?.forEach(d => allDates.add(d.date));
    overviewData?.costTrends?.openai?.forEach(d => allDates.add(d.date));
    return Array.from(allDates).sort();
  };

  const combinedDates = getCombinedDates();

  const costTrendData = {
    labels: combinedDates,
    datasets: [
      {
        label: 'Claude',
        data: combinedDates.map(date => {
          const item = overviewData?.costTrends?.claude?.find(d => d.date === date);
          return item?.cost_usd || 0;
        }),
        borderColor: 'rgb(217, 119, 87)',
        backgroundColor: 'rgba(217, 119, 87, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Gemini',
        data: combinedDates.map(date => {
          const item = overviewData?.costTrends?.gemini?.find(d => d.date === date);
          return item?.cost_usd || 0;
        }),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.1)',
        tension: 0.4,
      },
      {
        label: 'OpenAI',
        data: combinedDates.map(date => {
          const item = overviewData?.costTrends?.openai?.find(d => d.date === date);
          return item?.cost_usd || 0;
        }),
        borderColor: 'rgb(16, 163, 127)',
        backgroundColor: 'rgba(16, 163, 127, 0.1)',
        tension: 0.4,
      },
    ],
  };

  return (
    <div className="overview-dashboard">
      <div className="overview-header">
        <h3>All Providers Overview</h3>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="overview-days-selector"
        >
          <option value={7}>7d</option>
          <option value={14}>14d</option>
          <option value={30}>30d</option>
        </select>
      </div>

      <div className="overview-summary">
        <div className="overview-metric">
          <div className="metric-label">Total Cost</div>
          <div className="metric-value">${totalCost.toFixed(2)}</div>
        </div>
        <div className="overview-metric">
          <div className="metric-label">Total Sessions</div>
          <div className="metric-value">{totalSessions.toLocaleString()}</div>
        </div>
        <div className="overview-metric">
          <div className="metric-label">Total Tokens</div>
          <div className="metric-value">{totalTokens.toLocaleString()}</div>
        </div>
      </div>

      <div className="overview-breakdown">
        <div className="provider-breakdown claude">
          <div className="provider-breakdown-header">
            <span className="provider-icon">🤖</span>
            <span className="provider-name">Claude</span>
          </div>
          <div className="provider-cost">${(overviewData?.claude?.total_cost_usd || 0).toFixed(2)}</div>
        </div>
        <div className="provider-breakdown gemini">
          <div className="provider-breakdown-header">
            <span className="provider-icon">💎</span>
            <span className="provider-name">Gemini</span>
          </div>
          <div className="provider-cost">${(overviewData?.gemini?.total_cost_usd || 0).toFixed(2)}</div>
        </div>
        <div className="provider-breakdown openai">
          <div className="provider-breakdown-header">
            <span className="provider-icon">🔮</span>
            <span className="provider-name">OpenAI</span>
          </div>
          <div className="provider-cost">${(overviewData?.openai?.total_cost_usd || 0).toFixed(2)}</div>
        </div>
      </div>

      <div className="overview-chart">
        <h4>Cost Trends (30 Days)</h4>
        <Line
          data={costTrendData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                labels: {
                  color: '#ffffff',
                  font: { size: 11 },
                  padding: 10,
                  usePointStyle: true,
                }
              },
            },
            scales: {
              x: {
                ticks: {
                  color: '#999',
                  font: { size: 10 },
                  maxRotation: 45,
                  minRotation: 45,
                },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              },
              y: {
                ticks: {
                  color: '#999',
                  font: { size: 10 },
                  callback: function (value) {
                    return '$' + value.toFixed(2);
                  },
                },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default OverviewDashboard;
